import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useWebSocket } from '../hooks/useWebSocket';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const POLL_MS = 20000;
const TEMP_COLOR = '#f97316';
const HUM_COLOR = '#06b6d4';

function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit', hour12: false });
}
function fmtDateTime(ts) {
  if (!ts) return '–';
  return new Date(ts).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const on = status === 'online';
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${on ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
      <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${on ? 'bg-green-500' : 'bg-red-500'}`} />
      {on ? 'Online' : 'Offline'}
    </span>
  );
}

function SeverityBadge({ severity }) {
  if (!severity) return null;
  const cls = severity === 'WARNING'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{severity}</span>;
}

function ValueCard({ label, value, unit, color, alarm, warning, icon }) {
  const isAlarm = alarm != null && value != null && (color === TEMP_COLOR ? value >= alarm : value >= alarm);
  const isWarning = warning != null && value != null && !isAlarm && (color === TEMP_COLOR ? value >= warning : value >= warning);
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-5 flex flex-col items-center gap-2 ${isAlarm ? 'border-red-400 dark:border-red-500' : isWarning ? 'border-amber-400 dark:border-amber-500' : 'border-gray-200 dark:border-gray-700'
      }`}>
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isAlarm ? 'bg-red-100 dark:bg-red-900/30' : isWarning ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
        {icon}
      </div>
      <p className={`text-3xl font-bold ${isAlarm ? 'text-red-500' : isWarning ? 'text-amber-500' : ''}`} style={!isAlarm && !isWarning ? { color } : {}}>
        {value != null ? `${value.toFixed(1)}${unit}` : '–'}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</p>
      {isAlarm && <span className="text-xs text-red-500 font-semibold">⚠ ALARM</span>}
      {isWarning && <span className="text-xs text-amber-500 font-semibold">⚠ WARNING</span>}
    </div>
  );
}

export default function SensorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [sensor, setSensor] = useState(null);
  const [readings, setReadings] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [dataLogger, setDataLogger] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }), []);

  const fetchAll = useCallback(async () => {
    try {
      const [sRes, rRes, aRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/sensors/${id}`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/readings/${id}?hours=24`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/alarms?sensor_id=${id}&resolved=false`, { headers: headers() }),
      ]);
      setSensor(sRes.data);
      const raw = Array.isArray(rRes.data) ? rRes.data : [];
      setReadings(raw.map(r => ({
        ...r,
        time: fmtTime(r.ts),
        temperature: r.temperature != null ? parseFloat(r.temperature.toFixed(1)) : null,
        humidity: r.humidity != null ? parseFloat(r.humidity.toFixed(1)) : null,
      })));
      setAlarms(Array.isArray(aRes.data) ? aRes.data : []);

      // Fetch data logger and company
      if (sRes.data.data_logger_id) {
        try {
          const dlRes = await axios.get(`${BACKEND_URL}/api/data-loggers/${sRes.data.data_logger_id}`, { headers: headers() });
          setDataLogger(dlRes.data);
        } catch { }
      }
      if (sRes.data.company_id) {
        try {
          const cRes = await axios.get(`${BACKEND_URL}/api/companies`, { headers: headers() });
          const cList = Array.isArray(cRes.data) ? cRes.data : [];
          setCompany(cList.find(c => c.id === sRes.data.company_id) || null);
        } catch { }
      }
    } catch (e) {
      toast.error('Gabim gjatë ngarkimit');
    } finally {
      setLoading(false);
    }
  }, [id, headers]);

  useEffect(() => {
    fetchAll();
    pollRef.current = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);

  // WebSocket — update-on live kur vjen reading i ri nga Pi për këtë sensor
  useWebSocket(useCallback((reading) => {
    if (reading.sensor_id !== id) return;
    const { temperature, humidity, ts } = reading;

    // Update vlerat aktuale të sensorit
    setSensor(prev => prev ? { ...prev, temperature, humidity, last_reading_ts: ts, status: 'online' } : prev);

    // Shto pikën e re te grafiku
    const point = {
      ts,
      time: fmtTime(ts),
      temperature: temperature != null ? parseFloat(temperature.toFixed(1)) : null,
      humidity: humidity != null ? parseFloat(humidity.toFixed(1)) : null,
    };
    setReadings(prev => [...prev.slice(-287), point]);
  }, [id]));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full p-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!sensor) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">Sensori nuk u gjet.</div>
      </Layout>
    );
  }

  const chartData = readings;
  // 50 most recent for history table
  const historyRows = [...readings].reverse().slice(0, 50);

  const alarmDot = ({ cx, cy, payload }) => {
    const temp = payload?.temperature;
    if (temp == null) return null;
    const isHighAlarm = sensor.temp_high_alarm != null && temp >= sensor.temp_high_alarm;
    const isLowAlarm = sensor.temp_low_alarm != null && temp <= sensor.temp_low_alarm;
    const isAlarm = isHighAlarm || isLowAlarm;
    const isWarn = !isAlarm && (
      (sensor.temp_high_warning != null && temp >= sensor.temp_high_warning) ||
      (sensor.temp_low_warning != null && temp <= sensor.temp_low_warning)
    );
    if (!isAlarm && !isWarn) return null;
    return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={4} fill={isAlarm ? '#ef4444' : '#f59e0b'} stroke="#111827" strokeWidth={1.5} />;
  };

  const chartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const tempEntry = payload.find(p => p.dataKey === 'temperature');
    const humEntry = payload.find(p => p.dataKey === 'humidity');
    const temp = tempEntry?.value;
    const isHighAlarm = temp != null && sensor.temp_high_alarm != null && temp >= sensor.temp_high_alarm;
    const isLowAlarm = temp != null && sensor.temp_low_alarm != null && temp <= sensor.temp_low_alarm;
    const isAlarm = isHighAlarm || isLowAlarm;
    const isHighWarn = !isAlarm && temp != null && sensor.temp_high_warning != null && temp >= sensor.temp_high_warning;
    const isLowWarn = !isAlarm && temp != null && sensor.temp_low_warning != null && temp <= sensor.temp_low_warning;
    const hasAlert = isAlarm || isHighWarn || isLowWarn;
    let alertLabel = null;
    if (isHighAlarm) alertLabel = `ALARM ↑ ${sensor.temp_high_alarm}°C`;
    else if (isLowAlarm) alertLabel = `ALARM ↓ ${sensor.temp_low_alarm}°C`;
    else if (isHighWarn) alertLabel = `WARNING ↑ ${sensor.temp_high_warning}°C`;
    else if (isLowWarn) alertLabel = `WARNING ↓ ${sensor.temp_low_warning}°C`;
    const borderColor = hasAlert ? (isAlarm ? '#ef4444' : '#f59e0b') : '#374151';
    return (
      <div style={{ background: '#1f2937', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
        <p style={{ color: '#9ca3af', marginBottom: 4 }}>{label}</p>
        {tempEntry && <p style={{ color: TEMP_COLOR }}>Temperatura: {temp != null ? `${temp}°C` : '–'}</p>}
        {humEntry && <p style={{ color: HUM_COLOR }}>Lagështia: {humEntry.value != null ? `${humEntry.value}%` : '–'}</p>}
        {hasAlert && (
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #374151' }}>
            <p style={{ color: '#d1d5db' }}>{sensor.name}</p>
            <p style={{ color: isAlarm ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>⚠ {alertLabel}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-full space-y-5">

        {/* Back + Header */}
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/devices')} className="mt-1 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{sensor.name}</h1>
              <StatusBadge status={sensor.status} />
              {alarms.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  {alarms.length} alarm{alarms.length > 1 ? 'e' : ''}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1 flex-wrap">
              <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded font-medium">{sensor.sensor_type}</span>
              {dataLogger && <span className="text-xs text-gray-500 dark:text-gray-400">DL: <strong className="text-gray-700 dark:text-gray-200">{dataLogger.name}</strong></span>}
              {company && <span className="text-xs text-gray-500 dark:text-gray-400">Kompania: <strong className="text-gray-700 dark:text-gray-200">{company.name}</strong></span>}
              {sensor.last_reading_ts && <span className="text-xs text-gray-400">Leximi i fundit: {fmtDateTime(sensor.last_reading_ts)}</span>}
            </div>
          </div>
          <button
            onClick={() => navigate(`/control?sensor=${id}`)}
            className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
            Setup Alarmeve
          </button>
        </div>

        {/* Value Cards */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <ValueCard
            label="Temperatura"
            value={sensor.temperature}
            unit="°C"
            color={TEMP_COLOR}
            alarm={sensor.temp_high_alarm}
            warning={sensor.temp_high_warning}
            icon={
              <svg className="w-7 h-7 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z" />
              </svg>
            }
          />
          <ValueCard
            label="Lagështia"
            value={sensor.humidity}
            unit="%"
            color={HUM_COLOR}
            alarm={sensor.humidity_high_alarm}
            warning={sensor.humidity_high_warning}
            icon={
              <svg className="w-7 h-7 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            }
          />
        </div>

        {/* Thresholds panel */}
        {(sensor.temp_high_alarm || sensor.temp_low_alarm || sensor.humidity_high_alarm || sensor.humidity_low_alarm) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Limitet e Konfigurura</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Temp max ALARM', value: sensor.temp_high_alarm, unit: '°C', cls: 'text-red-500' },
                { label: 'Temp min ALARM', value: sensor.temp_low_alarm, unit: '°C', cls: 'text-red-500' },
                { label: 'Temp max WARNING', value: sensor.temp_high_warning, unit: '°C', cls: 'text-amber-500' },
                { label: 'Temp min WARNING', value: sensor.temp_low_warning, unit: '°C', cls: 'text-amber-500' },
                { label: 'Lag. max ALARM', value: sensor.humidity_high_alarm, unit: '%', cls: 'text-red-500' },
                { label: 'Lag. min ALARM', value: sensor.humidity_low_alarm, unit: '%', cls: 'text-red-500' },
                { label: 'Lag. max WARNING', value: sensor.humidity_high_warning, unit: '%', cls: 'text-amber-500' },
                { label: 'Lag. min WARNING', value: sensor.humidity_low_warning, unit: '%', cls: 'text-amber-500' },
              ].filter(i => i.value != null).map((item, i) => (
                <div key={i} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2.5">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                  <p className={`text-lg font-bold ${item.cls}`}>{item.value}{item.unit}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Alarms */}
        {alarms.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-4">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              Alarme Aktive ({alarms.length})
            </h3>
            <div className="space-y-2">
              {alarms.map(a => (
                <div key={a.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <SeverityBadge severity={a.severity} />
                    <span className="text-sm text-red-700 dark:text-red-300">{a.message}</span>
                  </div>
                  <span className="text-xs text-red-400">{fmtDateTime(a.ts)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 24h Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white text-sm">{t('last24h')}</h2>
          </div>
          <div className="p-3">
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-sm text-gray-400">{t('noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#9ca3af' }} interval={Math.max(0, Math.floor(chartData.length / 6) - 1)} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <Tooltip content={chartTooltip} />
                  <Legend formatter={(v) => v === 'temperature' ? 'Temperatura (°C)' : 'Lagështia (%)'} wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="temperature" stroke={TEMP_COLOR} dot={alarmDot} activeDot={{ r: 4 }} strokeWidth={2} connectNulls />
                  <Line type="monotone" dataKey="humidity" stroke={HUM_COLOR} dot={false} strokeWidth={2} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* History Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Historiku (50 të fundit)</h2>
            <span className="text-xs text-gray-400">{readings.length} lexime / 24h</span>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            {historyRows.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">{t('noData')}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/80">
                  <tr>
                    {['Koha', 'Temperatura (°C)', 'Lagështia (%)'].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {historyRows.map((r, i) => {
                    const tempAlarm = sensor.temp_high_alarm && r.temperature >= sensor.temp_high_alarm;
                    const tempWarn = sensor.temp_high_warning && r.temperature >= sensor.temp_high_warning;
                    return (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs">{fmtDateTime(r.ts)}</td>
                        <td className="px-4 py-2">
                          {r.temperature != null ? (
                            <span className={`font-semibold ${tempAlarm ? 'text-red-500' : tempWarn ? 'text-amber-500' : 'text-orange-500'}`}>
                              {r.temperature.toFixed(1)}°C
                            </span>
                          ) : <span className="text-gray-400">–</span>}
                        </td>
                        <td className="px-4 py-2">
                          {r.humidity != null ? (
                            <span className="font-semibold text-cyan-500">{r.humidity.toFixed(1)}%</span>
                          ) : <span className="text-gray-400">–</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
}
